import { theme } from 'antd';
import type { GlobalToken } from 'antd';
import { CSSProperties, ReactNode } from 'react';
import styled from 'styled-components';

export type CardTitleProps = {
  /** Title text. */
  children: ReactNode;
  /** Native tooltip, e.g. the full title when it is clamped. */
  title?: string;
  className?: string;
  style?: CSSProperties;
};

/**
 * CardTitle — the title inside a card: base size, strong weight, in the
 * theme's font and heading color; at most two lines, then an ellipsis.
 *
 * @example
 * ```tsx
 * import { CardTitle } from '@libertify/ui';
 *
 * <CardTitle title={lessonTitle}>{lessonTitle}</CardTitle>
 * ```
 */
export const CardTitle = ({
  children,
  title,
  className,
  style,
}: CardTitleProps) => {
  const { token } = theme.useToken();

  return (
    <StyledCardTitle
      $token={token}
      title={title}
      className={className}
      style={style}
    >
      {children}
    </StyledCardTitle>
  );
};

export default CardTitle;

// ─── Styled components ───

const StyledCardTitle = styled.h5<{ $token: GlobalToken }>`
  margin: 0;
  font-family: ${({ $token }) => $token.fontFamily};
  font-size: ${({ $token }) => $token.fontSize}px;
  font-weight: ${({ $token }) => $token.fontWeightStrong};
  line-height: ${({ $token }) => $token.lineHeightHeading5};
  color: ${({ $token }) => $token.colorTextHeading};
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
`;
